import partition from 'lodash/partition';
import Notifications from 'react-notification-system-redux';
import { runTask } from '@myua/api';
import { api } from '../../../general/apiScenarios';
import { CHNode, CHNodeIsFolder, INodeToCHNode } from '../../../general/node';
import { _select, lm, ListenerApiLight } from '../../listenerMiddleware';
import { getNodeId } from '../app/selectors';
import { getCreativeFolder } from '../breadcrumbs/selectors';
import { updateWorkflow } from '../creativeWorkflow';
import * as fileManagerActions from '../fileManager/actions';
import { Messages } from '../creativeWorkflow/legacy_models/model';
import { createImportTask, parseErrorMessage, parseProgress, parseSuccess } from './api';
import * as googleDriveImportActions from './actions';

export const googleDriveImportListeners = () => {
  watchImportSubmitted();
  watchTaskCreated();
};

/**
 * Ноды по id, присланным в результате задачи.
 * Бэкенд возвращает только узлы верхнего уровня — то есть созданные непосредственно
 * в целевой папке, — поэтому одного запроса списка папки хватает на любое количество id;
 * точечного эндпоинта «нода по id» в `api.fs` нет.
 * Присланный id может не найтись в списке: ноду успели переименовать, переместить или
 * удалить, пока шла задача. Такая нода молча отбрасывается — редактор просто не получит
 * её в списке файлов.
 * `null` = список папки получить не удалось.
 */
async function loadImportedNodes(
  parentNodeId: CHNode['id'],
  importedNodeIds: CHNode['id'][],
): Promise<{ files: CHNode[]; folders: CHNode[] } | null> {
  const result = await api.fs.loadNodes(parentNodeId);

  if (!result.success) {
    return null;
  }

  const wanted = new Set(importedNodeIds);
  const created = result.data.response.nodes.filter((node) => wanted.has(node.id));
  const [folders, files] = partition(created, CHNodeIsFolder);

  return { files: files.map(INodeToCHNode), folders: folders.map(INodeToCHNode) };
}

/**
 * Пользователь мог уйти со страницы целевой папки: breadcrumbs описывают уже другое место,
 * поэтому креативный флоу запускаем только если целевая папка всё ещё открыта.
 */
const isOnTargetFolder = (lapi: ListenerApiLight, parentNodeId: CHNode['id']) =>
  _select(lapi, getNodeId) === parentNodeId;

/**
 * Целевая папка импорта — папка креатива, и пользователь всё ещё в ней.
 * Оба условия проверяются вместе, потому что `getCreativeFolder` вычисляется по breadcrumbs
 * текущего вида и описывает целевую папку только пока она открыта.
 */
const targetIsCreativeFolder = (lapi: ListenerApiLight, parentNodeId: CHNode['id']) =>
  isOnTargetFolder(lapi, parentNodeId) && _select(lapi, getCreativeFolder) !== undefined;

function watchImportSubmitted() {
  lm.takeLeading(googleDriveImportActions.importSubmitted, async ({ payload }, lapi) => {
    const parentNodeId = _select(lapi, getNodeId);

    if (parentNodeId === null) {
      return;
    }

    // Диспатчим до сетевых запросов, чтобы кнопка Import сразу показала loading.
    lapi.dispatch(googleDriveImportActions.taskCreationStarted());

    const created = await createImportTask({ link: payload.link, parentNodeId });

    if (!created.ok) {
      lapi.dispatch(googleDriveImportActions.taskCreationFailed({ errorMessage: created.error }));
      return;
    }

    lapi.dispatch(
      googleDriveImportActions.taskCreated({
        taskId: created.taskId,
        directUploadingId: created.directUploadingId,
        link: payload.link,
        parentNodeId,
        createdAt: Date.now(),
      }),
    );
  });
}

function watchTaskCreated() {
  lm.takeEvery(googleDriveImportActions.taskCreated, async ({ payload }, lapi) => {
    const { dispatch } = lapi;

    const taskResult = await runTask(payload.taskId, 2000, undefined, ({ status, data }) => {
      if (status !== 'STARTED') {
        return;
      }

      const parsed = parseProgress(data);

      if (parsed) {
        dispatch(
          googleDriveImportActions.taskProgressChanged({
            taskId: payload.taskId,
            progress: parsed.percent,
          }),
        );
      }
    });

    if (!taskResult.success) {
      const errorMessage = parseErrorMessage(taskResult.data, taskResult.error);

      dispatch(googleDriveImportActions.taskFailed({ taskId: payload.taskId, errorMessage }));
      dispatch(
        Notifications.error({
          title: 'Failed to import from Google Drive',
          message: errorMessage,
        }),
      );
      dispatch(fileManagerActions.refreshRequested());
      return;
    }

    dispatch(googleDriveImportActions.taskSucceeded({ taskId: payload.taskId }));
    dispatch(fileManagerActions.refreshRequested());

    await handleImportSucceeded(lapi, payload.parentNodeId, parseSuccess(taskResult.data));
  });
}

/**
 * Пост-обработка успешного импорта: если целевая папка — папка креатива,
 * открыть MetadataEditor по той же механике, что drag&drop-загрузка (`uploadWorkflow`).
 */
async function handleImportSucceeded(
  lapi: ListenerApiLight,
  parentNodeId: CHNode['id'],
  importedNodeIds: CHNode['id'][],
) {
  const importFinished = () =>
    lapi.dispatch(Notifications.success({ title: 'Import from Google Drive finished' }));

  // Ранний выход: пустой список (ссылка вела на пустой файл — бэкенд его пропускает и узел
  // не создаёт) открывать нечем, а вне папки креатива редактор не нужен —
  // в обоих случаях не тратим на импорт лишний запрос списка нод.
  if (importedNodeIds.length === 0 || !targetIsCreativeFolder(lapi, parentNodeId)) {
    importFinished();
    return;
  }

  const imported = await loadImportedNodes(parentNodeId, importedNodeIds);

  if (imported === null) {
    importFinished();
    return;
  }

  if (imported.folders.length > 0) {
    // Папка креатива не принимает вложенные папки — так же ведёт себя загрузка файлов.
    lapi.dispatch(Notifications.warning(Messages.cantImportFolders()));
  }

  if (imported.files.length === 0) {
    importFinished();
    return;
  }

  // Гейт непосредственно перед редактором: пока грузился список нод, пользователь мог уйти из папки.
  if (!targetIsCreativeFolder(lapi, parentNodeId)) {
    importFinished();
    return;
  }

  // Передаётся именно parentNodeId: loadBLContext сам находит креатив по иерархии парентов.
  // Ожидание своей очереди к редактору обеспечивает сам MetadataEditorService,
  // отдельная очередь здесь не нужна.
  const metadataApplied = await updateWorkflow(lapi, imported.files, parentNodeId, {
    notify: false,
  });

  if (!metadataApplied) {
    // Отмена в редакторе или неуспех привязки: success-нотификацию не показываем.
    // Та же логика, что в uploadWorkflow при `notify: false`.
    return;
  }

  lapi.dispatch(Notifications.success({ title: 'All creatives have been created successfully' }));
}
